import { useContext } from "react";
import { map } from "lodash";
import { useTranslation } from "react-i18next";
import { SUBMISSION_CATEGORY } from "common/constants";
import { RECORDS_TAB_NAME } from "common/routing";
import useNavigationTab from "../../../hooks/useNavigationTabs";
import { getOptionalTabs } from "../../../utils/submissions";
import Improvements from "../../AddressPanel/Improvements";
import Submissions from "../../AddressPanel/Submissions";
import { AuthContext } from "../../Authorization/AuthContext";
import TabGroup from "../../Common/TabGroup";
import { GuestPropertyTabProps, RecordsTabProps } from ".";
import { TabContainer, TabGroupContainer } from "./__styles__/PropertyPage";

const SISD_CATEGORY =
  SUBMISSION_CATEGORY.SUBSTANTIAL_IMPROVEMENT_SUBSTANTIAL_DAMAGE;

export const Records = ({
  latitude,
  longitude,
  propertyLabel,
  submissions,
}: GuestPropertyTabProps & RecordsTabProps) => {
  const { account } = useContext(AuthContext);
  const { t } = useTranslation();

  const defaultVisibleSubmissionTypeCategories =
    account?.submissionTypes
      .filter(submissionType => !submissionType.hiddenFromPublicByDefault)
      .map(st => st.category) ?? [];
  const existingSubmissionTypeCategories = submissions.map(
    submission => submission.submissionTypeVersion.submissionType.category,
  );
  const categories = new Set([
    ...defaultVisibleSubmissionTypeCategories,
    ...existingSubmissionTypeCategories,
  ]);

  if (account?.publicPortal.hideSISD) {
    categories.delete(SISD_CATEGORY);
  } else {
    categories.add(SISD_CATEGORY);
  }

  const optionalTabs = getOptionalTabs(t);
  const tabs = optionalTabs
    .filter(({ category }) => categories.has(category))
    .map(({ recordTabName, title }) => ({
      value: recordTabName,
      label: title,
    }));

  const defaultTab = tabs[0];

  const [tab, setTab] = useNavigationTab({
    defaultTab: defaultTab?.value,
    allowedTabs: map(tabs, "value"),
    queryParamName: "recordTab",
  });

  if (!tab) {
    // This should never happen, as the records tab shouldn't show if
    // there aren't available tabs, so this is just a safe-guard
    return null;
  }

  const currentOptionalTab = optionalTabs.find(
    ({ category, recordTabName }) =>
      tab === recordTabName && category !== SISD_CATEGORY,
  );

  return (
    <>
      <TabGroupContainer data-testid="tabs" hasOutlineStyleActiveTabs>
        <TabGroup
          currentTab={tab}
          setTab={setTab}
          tabs={tabs}
          activeTabStyle="outline"
        />
      </TabGroupContainer>
      <TabContainer data-testid="records">
        {tab === RECORDS_TAB_NAME.IMPROVEMENTS && (
          <Improvements
            latitude={latitude}
            longitude={longitude}
            address={propertyLabel}
          />
        )}
        {currentOptionalTab && (
          <Submissions
            category={currentOptionalTab.category}
            title={currentOptionalTab.title}
            titleWithinCopy={currentOptionalTab.titleWithinCopy}
            latitude={latitude}
            longitude={longitude}
            address={propertyLabel}
          />
        )}
      </TabContainer>
    </>
  );
};
